import { useCallback, useEffect, useState } from 'react';
import { api, type VariantMappingRow } from '../lib/api.js';

interface Draft {
  sku: string;
  color_id: string;
  platform: 'rifle' | 'handgun' | 'pcc' | '*';
  bc_product_id: number;
  bc_variant_id: number;
  bc_custom_field_diameter_id: number | null;
  bc_custom_field_length_id: number | null;
  bc_modifier_suppressor_model_id: number | null;
  bc_modifier_cover_size_id: number | null;
  bc_modifier_rear_mount_id: number | null;
  bc_modifier_rear_none_value_id: number | null;
  bc_modifier_rear_qd_small_value_id: number | null;
  bc_modifier_rear_qd_large_value_id: number | null;
  bc_modifier_front_coverage_id: number | null;
  bc_modifier_front_covered_value_id: number | null;
  bc_modifier_front_exposed_value_id: number | null;
  bc_modifier_front_flow_through_value_id: number | null;
}

const EMPTY: Draft = {
  sku: '',
  color_id: '*',
  platform: '*',
  bc_product_id: 0,
  bc_variant_id: 0,
  bc_custom_field_diameter_id: null,
  bc_custom_field_length_id: null,
  bc_modifier_suppressor_model_id: null,
  bc_modifier_cover_size_id: null,
  bc_modifier_rear_mount_id: null,
  bc_modifier_rear_none_value_id: null,
  bc_modifier_rear_qd_small_value_id: null,
  bc_modifier_rear_qd_large_value_id: null,
  bc_modifier_front_coverage_id: null,
  bc_modifier_front_covered_value_id: null,
  bc_modifier_front_exposed_value_id: null,
  bc_modifier_front_flow_through_value_id: null,
};

export function VariantMappingsPage(): JSX.Element {
  const [items, setItems] = useState<VariantMappingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [skuFilter, setSkuFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const query: Parameters<typeof api.variantMappings.list>[0] = { limit: 200 };
      if (skuFilter) query.sku = skuFilter;
      const r = await api.variantMappings.list(query);
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      setError(String(err));
    }
  }, [skuFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <h1>Variant Mappings</h1>
      <p style={{ color: 'var(--muted)' }}>
        Maps the configurator's resolved SKU + color + platform to BigCommerce product and variant
        IDs for native add-to-cart. Wildcards (<code>*</code>) act as fallbacks; an exact match wins.
      </p>
      {error && <div className="notice error">{error}</div>}

      <div className="toolbar">
        <input
          className="input"
          placeholder="Filter by SKU"
          value={skuFilter}
          onChange={(e) => setSkuFilter(e.target.value)}
        />
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>{total} mapping{total === 1 ? '' : 's'}</span>
      </div>

      <table>
        <thead>
          <tr>
            <th>SKU</th><th>Color</th><th>Platform</th>
            <th>BC product</th><th>BC variant</th>
            <th>Ø field</th><th>Len field</th>
            <th>Model modifier</th><th>Size modifier</th>
            <th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id}>
              <td className="sku">{m.sku}</td>
              <td>{m.color_id}</td>
              <td>{m.platform}</td>
              <td>{m.bc_product_id}</td>
              <td>{m.bc_variant_id}</td>
              <td>{m.bc_custom_field_diameter_id ?? '—'}</td>
              <td>{m.bc_custom_field_length_id ?? '—'}</td>
              <td>{m.bc_modifier_suppressor_model_id ?? '—'}</td>
              <td>{m.bc_modifier_cover_size_id ?? '—'}</td>
              <td>{m.is_active === 0 ? <span className="badge inactive">Inactive</span> : 'Active'}</td>
              <td>
                {m.is_active !== 0 && (
                  <button className="danger" onClick={async () => {
                    if (!confirm(`Disable mapping for ${m.sku} / ${m.color_id} / ${m.platform}?`)) return;
                    await api.variantMappings.softDelete(m.id);
                    void refresh();
                  }}>Disable</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ marginTop: 32 }}>Add mapping</h2>
      <div className="fields" style={{ maxWidth: 800 }}>
        <label>SKU (or <code>*</code>)
          <input className="sku" value={draft.sku} onChange={(e) => setDraft({ ...draft, sku: e.target.value })} placeholder="BPG-SC-M-8-1-38" />
        </label>
        <label>Color ID (or <code>*</code>)
          <input value={draft.color_id} onChange={(e) => setDraft({ ...draft, color_id: e.target.value })} />
        </label>
        <label>Platform
          <select value={draft.platform} onChange={(e) => setDraft({ ...draft, platform: e.target.value as Draft['platform'] })}>
            <option value="*">* (any)</option>
            <option value="rifle">rifle</option>
            <option value="handgun">handgun</option>
            <option value="pcc">pcc</option>
          </select>
        </label>
        <label>BC product ID
          <input type="number" value={draft.bc_product_id} onChange={(e) => setDraft({ ...draft, bc_product_id: Number(e.target.value) })} />
        </label>
        <label>BC variant ID
          <input type="number" value={draft.bc_variant_id} onChange={(e) => setDraft({ ...draft, bc_variant_id: Number(e.target.value) })} />
        </label>
        <label>BC custom field — diameter
          <input type="number" placeholder="optional" value={draft.bc_custom_field_diameter_id ?? ''} onChange={(e) => setDraft({ ...draft, bc_custom_field_diameter_id: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
        <label>BC custom field — length
          <input type="number" placeholder="optional" value={draft.bc_custom_field_length_id ?? ''} onChange={(e) => setDraft({ ...draft, bc_custom_field_length_id: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
        <label>BC modifier — suppressor model
          <input type="number" placeholder="optional" value={draft.bc_modifier_suppressor_model_id ?? ''} onChange={(e) => setDraft({ ...draft, bc_modifier_suppressor_model_id: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
        <label>BC modifier — cover size
          <input type="number" placeholder="optional" value={draft.bc_modifier_cover_size_id ?? ''} onChange={(e) => setDraft({ ...draft, bc_modifier_cover_size_id: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
        {([
          ['bc_modifier_rear_mount_id', 'BC modifier — rear mount'],
          ['bc_modifier_rear_none_value_id', 'Rear value — none / standard'],
          ['bc_modifier_rear_qd_small_value_id', 'Rear value — QD small'],
          ['bc_modifier_rear_qd_large_value_id', 'Rear value — QD large'],
          ['bc_modifier_front_coverage_id', 'BC modifier — front coverage'],
          ['bc_modifier_front_covered_value_id', 'Front value — covered'],
          ['bc_modifier_front_exposed_value_id', 'Front value — exposed'],
          ['bc_modifier_front_flow_through_value_id', 'Front value — flow-through'],
        ] as const).map(([field, label]) => (
          <label key={field}>{label}
            <input
              type="number"
              placeholder="optional"
              value={draft[field] ?? ''}
              onChange={(e) => setDraft({ ...draft, [field]: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </label>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          className="primary"
          disabled={busy || !draft.sku || !draft.color_id || !draft.bc_product_id || !draft.bc_variant_id}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.variantMappings.create(draft);
              setDraft(EMPTY);
              void refresh();
            } catch (err) {
              setError(String(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Create mapping'}
        </button>
      </div>
    </>
  );
}
